import { ErrorView, Loading } from "@alch/ui";
import ReactQueryErrorView from "@components/ui/react-query-error-view/ReactQueryErrorView";
import PageHeader from "@features/page-header";
import { useScopedApp } from "@hooks/useScopedApp.ts";
import { useNetworkInfos } from "@queries/useNetwork";
import { DEPLOY_ENV } from "@util/deployEnv";
import { Path } from "@util/paths";
import { TestId } from "@util/testIds";
import { memo, useMemo } from "react";
import ComposerForm from "./ComposerForm.js";
import type { ComposerMethodConfig } from "./lib/methods.js";
import useComposerMethodConfigs from "./useComposerMethodConfigs.js";

const defaultBaseUrl = {
  production: `https://dashboard.alchemy.com${Path.Sandbox}`,
  staging: `https://dashboard.alchemypreview.com${Path.Sandbox}`,
  development: `http://localhost:8001${Path.Sandbox}`,
}[DEPLOY_ENV];

interface ComposerProps {
  baseUrl?: string;
  isPublic?: boolean;
  filterMethods?: (methods: ComposerMethodConfig[]) => ComposerMethodConfig[];
  defaultMethod?: string;
}

const Composer = ({
  baseUrl,
  isPublic,
  filterMethods,
  defaultMethod,
}: ComposerProps) => {
  const networkInfos = useNetworkInfos();
  const scopedApp = useScopedApp({
    enabled: !isPublic,
  });
  const methodConfigs = useComposerMethodConfigs({
    networkInfos: networkInfos.data,
  });

  const queries = [methodConfigs, networkInfos];

  const filteredMethodConfigs = useMemo(
    () =>
      filterMethods
        ? filterMethods(methodConfigs.data || [])
        : methodConfigs.data || [],
    [methodConfigs.data, filterMethods],
  );

  return (
    <div data-testid={TestId.SandboxPage}>
      {baseUrl ? null : <PageHeader title="Sandbox" />}

      {scopedApp?.isPending || queries.some((q) => q.isLoading) ? (
        <Loading className="mx-auto mt-4" />
      ) : queries.some((q) => q.data === undefined) ? (
        <ReactQueryErrorView results={queries} />
      ) : filteredMethodConfigs.length === 0 ? (
        <ErrorView
          title="Failed to load API specs"
          onRetry={methodConfigs.refetch}
        />
      ) : (
        <ComposerForm
          baseUrl={baseUrl || defaultBaseUrl}
          methodConfigs={filteredMethodConfigs}
          networkInfos={networkInfos.data || []}
          isPublic={isPublic}
          defaultMethod={defaultMethod}
        />
      )}
    </div>
  );
};

export default memo(Composer);
